import { FormSection, FormRow, MediumFormRow, BigFormRow } from 'framework/ui/layout';
import { NumberInput, EnumDropdown, ClearAfterUnmountWrapper, InputList } from 'framework/form';
import { WorkingConditionsInputListTemplate } from '../shared/inputs/WorkingConditionsInputs';
import ProjectDescriptionInputs from '../shared/inputs/ProjectDescriptionInputs';
import { SkillsInput } from 'domain/recruiting/shared/skills';
import { NoteAttachmentUploaderInput } from 'domain/shared/artifact/input';
import { COMPETENCY_LEVEL, EXPIRATION_PERIOD_OPTIONS, HIRING_MODE } from 'domain/shared/newVacancy';
import SpecializationDropdown from '../shared/inputs/SpecializationDropdown';
import VacancyWysiwygInput from '../../vacancy/shared/input/VacancyWysiwygInput';
import { TechnologyDropdown } from 'domain/shared/career';
import TargetHireDateInput from '../shared/inputs/TargetHireDateInput';
import { RowGroupInputsWrapper } from 'framework/ui/ui-builder';
import useCommonSkillsService from 'domain/recruiting/candidate/commonskills/commonSkillsService';
import { ENGLISH_LEVEL_SHORT_OPTIONS } from 'domain/shared/core/constants';

const VacancyForm = () => {
  const { searchByName } = useCommonSkillsService();

  return (
    <>
      <FormSection title="Main Information">
        <FormRow>
          <SpecializationDropdown source="specialization" tooltip="Select Specialization or create new." />
          <TechnologyDropdown
            source="technology"
            idSource="name"
            isVisible={data => data.specialization === 'Software Engineer'}
          />
          <ClearAfterUnmountWrapper>
            <EnumDropdown
              source="competencyLevel"
              options={COMPETENCY_LEVEL}
              label="Competency level"
              placeholder="Select competency level"
              isRequired
            />
          </ClearAfterUnmountWrapper>
        </FormRow>

        <FormRow>
          <EnumDropdown
            options={EXPIRATION_PERIOD_OPTIONS}
            source="expirationPeriodInMonth"
            label="Expiration period (Month)"
            placeholder="Select period"
          />

          <EnumDropdown
            tooltip={
              <>
                Active hiring mode means that a new position is available and the person is needed to fill it.
                <br />
                Bar raiser means that no additional positions are available, but the staffing coordinator will replace
                low-performers with the hired person.
              </>
            }
            options={HIRING_MODE}
            source="hiringMode"
            label="Hiring mode"
            placeholder="Select Mode"
          />
        </FormRow>

        <FormRow>
          <TargetHireDateInput />

          <RowGroupInputsWrapper isRequired wrapperLabel="People to hire">
            <NumberInput className="group-input-label" source="peopleToHireMin" label="Min" />
            <NumberInput className="group-input-label" source="peopleToHireMax" label="Max" />
          </RowGroupInputsWrapper>
        </FormRow>
      </FormSection>

      <FormSection title="Working Conditions">
        <FormRow colMd={12} colXl={12}>
          <ClearAfterUnmountWrapper>
            <InputList
              minFields={1}
              source="workingConditions"
              addText="Add location"
              maxFields={10}
              inputTemplate={WorkingConditionsInputListTemplate}
            />
          </ClearAfterUnmountWrapper>
        </FormRow>
      </FormSection>

      <FormSection title="Project Description">
        <ClearAfterUnmountWrapper>
          <InputList source="projects" addText="Add project" maxFields={10} inputTemplate={ProjectDescriptionInputs} />
        </ClearAfterUnmountWrapper>
      </FormSection>

      <FormSection title="Responsibilities*">
        <BigFormRow>
          <VacancyWysiwygInput source="responsibilities" label="" />
        </BigFormRow>
      </FormSection>

      <FormSection title="Mandatory Requirements">
        <BigFormRow>
          <VacancyWysiwygInput source="mandatoryRequirements.description" label="" />
        </BigFormRow>

        <MediumFormRow>
          <SkillsInput
            source="mandatoryRequirements.skills"
            loadSuggestionsMethod={searchByName}
            label="Skills"
            helpText="Required skills for the Vacancy. These skills will be used for Candidate matching purposes and will not be publicly displayed."
          />
        </MediumFormRow>

        <FormRow>
          <NumberInput source="mandatoryRequirements.requiredExperienceInYears" label="Required experience (years)" />

          <EnumDropdown
            source="mandatoryRequirements.englishLevel"
            label="English level"
            options={ENGLISH_LEVEL_SHORT_OPTIONS}
            placeholder="Choose english level"
          />
        </FormRow>
      </FormSection>

      <FormSection title="Optional Requirements">
        <BigFormRow>
          <VacancyWysiwygInput source="optionalRequirements.description" label="" />
        </BigFormRow>

        <MediumFormRow>
          <SkillsInput
            source="optionalRequirements.skills"
            loadSuggestionsMethod={searchByName}
            label="Skills:"
            helpText="Optional skills for the Vacancy. These skills will be used for Candidate matching purposes and will not be publicly displayed."
          />
        </MediumFormRow>
      </FormSection>

      <FormSection title="Notes">
        <BigFormRow>
          <VacancyWysiwygInput source="notes.description" label="" />
        </BigFormRow>
        <FormRow>
          <NoteAttachmentUploaderInput label="Attachments" source="notes.attachmentIds" />
        </FormRow>
      </FormSection>
    </>
  );
};

export default VacancyForm;
